import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";

export interface EvidenceCaption {
  store?: string | number;
  asset?: string | number;
  leaf?: string;
  captureDate?: string; // deferred to v2 substrate JOIN
}

export interface EvidencePayload {
  urls: string[];
  caption?: EvidenceCaption;
}

interface EvidenceContextValue {
  open: (payload: EvidencePayload) => void;
  close: () => void;
}

const EvidenceContext = createContext<EvidenceContextValue | null>(null);

/**
 * Shared lightbox provider. Wrap the dashboard surface in one of these and
 * any EvidenceBadge can fire useEvidence().open({ urls, caption }) — the
 * single Radix Dialog mounted here flips open with the carousel + caption.
 */
export function EvidenceProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<EvidencePayload | null>(null);
  const [index, setIndex] = useState(0);

  const open = useCallback((p: EvidencePayload) => {
    setPayload(p);
    setIndex(0);
  }, []);
  const close = useCallback(() => setPayload(null), []);

  const total = payload?.urls.length ?? 0;
  const url = payload?.urls[index];

  const prev = () => setIndex((i) => (total > 0 ? (i - 1 + total) % total : 0));
  const next = () => setIndex((i) => (total > 0 ? (i + 1) % total : 0));

  return (
    <EvidenceContext.Provider value={{ open, close }}>
      {children}
      <Dialog open={payload !== null} onOpenChange={(o) => (!o ? close() : undefined)}>
        <DialogContent
          className="max-w-3xl border-border/40 bg-card p-0 overflow-hidden"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
          }}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border/40 px-5 py-3">
            <div className="min-w-0">
              <DialogTitle className="truncate text-sm font-semibold">
                {payload?.caption?.leaf ?? "Evidence photo"}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                {captionLine(payload?.caption, index, total)}
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={close}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative flex aspect-[4/3] w-full items-center justify-center bg-background">
            {url ? (
              <img
                src={url}
                alt={payload?.caption?.leaf ?? "Evidence photo"}
                className="max-h-full max-w-full object-contain"
                loading="lazy"
              />
            ) : null}
            {total > 1 ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 bg-background/70 hover:bg-background/90",
                  )}
                  onClick={prev}
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 bg-background/70 hover:bg-background/90",
                  )}
                  onClick={next}
                  aria-label="Next photo"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </EvidenceContext.Provider>
  );
}

export function useEvidence() {
  const ctx = useContext(EvidenceContext);
  if (!ctx) throw new Error("useEvidence must be inside <EvidenceProvider>");
  return ctx;
}

function captionLine(c: EvidenceCaption | undefined, index: number, total: number): string {
  const parts: string[] = [];
  if (c?.store !== undefined) parts.push(`Store ${c.store}`);
  if (c?.asset !== undefined) parts.push(`Asset ${c.asset}`);
  if (c?.captureDate) parts.push(c.captureDate);
  if (total > 1) parts.push(`${index + 1} / ${total}`);
  return parts.join(" · ");
}
